import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createElementBlock as _createElementBlock, withModifiers as _withModifiers, Teleport as _Teleport, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-c2f8c9d0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4" }
const _hoisted_2 = { class: "flex items-center gap-2 my-4 md:my-8" }
const _hoisted_3 = {
  key: 0,
  class: "flex flex-col md:flex-row items-center gap-4 mt-4 md:mt-0"
}
const _hoisted_4 = { class: "px-3 py-1 bg-white border border-gray-200 rounded-md text-sm font-medium text-gray-700 shadow-sm" }
const _hoisted_5 = { class: "mt-4 flex flex-col gap-4" }
const _hoisted_6 = { class: "flex flex-wrap items-center gap-x-4 gap-y-2" }
const _hoisted_7 = {
  key: 0,
  class: "flex"
}
const _hoisted_8 = {
  key: 0,
  class: "italic text-gray-500",
  "data-testid": "consult-unidentified-name"
}
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "flex justify-center p-4" }
const _hoisted_11 = {
  key: 3,
  "data-testid": "consult-empty-state",
  class: "flex flex-col items-center justify-center p-6 md:p-12 rounded-lg text-center"
}
const _hoisted_12 = { class: "text-xl font-semibold text-gray-700 mb-2" }
const _hoisted_13 = { class: "text-sm text-gray-500 mb-4" }

import { mdiOfficeBuildingPlusOutline, mdiFilterOutline, mdiMapMarkerRadiusOutline } from '@mdi/js'
import EditIcon from '@/assets/icons/edit.vue'
import DeleteIcon from '@/assets/icons/delete.vue'
import CheckShiftSkeleton from '@/components/designSystem/Skeleton/CheckShiftSkeleton.vue'
import { ref, onMounted, watch, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useStationStore, CONSULTING_LOAD_ERROR } from '@/pinia-store/useStationStore'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import { ConsultingRequest } from '@/Network/Types/Requests/StationRequests'
import type { RowConsultingResponse } from '@/Network/Types/Responses/StationResponses'
import UpdateBulkStationModal from './modals/update.bulk.station.modal.vue'
import RemoveBulkStationModal from './modals/remove.bulk.station.modal.vue'
import CheckAttemptsModal from './modals/check.attempts.modal.vue'
import FilterStationModal from '@/components/Filters/filter.stations.vue'
import { useModalStore, EModals } from '@/pinia-store/useModalStore'
import { computed } from 'vue'
import { VDataTable } from 'vuetify/lib/labs/components.mjs'
import { useFilterStationsStore } from '@/components/Filters/useFilterStation'
import { useRoute } from 'vue-router'
import {
  UNIDENTIFIED_NAME_LABEL,
  consultValueCell,
  withCoverageHeaders
} from './consultCoverageColumns'
import { isUnidentifiedCoverage } from '@/utils/coverage'
import FiltersUsed from './filters.used.vue'
import { rowSector, rowSpecialty } from './modals/useStation'
import { catalogDisplayName } from '@/utils/stationDisplay'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

// A11: colunas e filtros de setor/especialidade só com a flag ligada.
const itemsPerPage = 20

export default /*#__PURE__*/_defineComponent({
  __name: 'CheckShift',
  setup(__props) {

const isDashboardV2 = useFeatureFlags().flags.enabledDashboardV2
const SECTOR_SPECIALTY_HEADER_KEYS = ['sectorName', 'specialtyName']

const isMobile = ref(window.innerWidth < 768)
const handleResize = () => {
  isMobile.value = window.innerWidth < 768
}
window.addEventListener('resize', handleResize)

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

const route = useRoute()
const useFilterStations = useFilterStationsStore()
const { filters } = storeToRefs(useFilterStations)

const hasFilter = computed(() => {
  return Object.values(filters.value).some((f) => f)
})

// type Headers = InstanceType<typeof VDataTable>['headers']

// A aba precisa de periodo: sem `startDate`/`endDate` a API responde
// "startDate and endDate are required for consulting" e o erro cru vazava para o
// ReportErrorState. Sem periodo nao ha consulta — so o convite a filtrar.
const hasPeriod = computed(() => Boolean(filters.value.startDate && filters.value.endDate))

const emptyStateTitle = computed(() =>
  hasPeriod.value
    ? 'Nenhum plantão encontrado'
    : 'Selecione um período nos filtros para consultar os plantões'
)

const emptyStateMessage = computed(() =>
  hasPeriod.value
    ? 'Você pode utilizar os filtros para refinar sua busca.'
    : 'A consulta precisa de uma data inicial e uma data final.'
)

onMounted(async () => {
  // Deep link (/consultar?company=…&startDate=…): restaura os filtros da URL
  // antes do primeiro load para consultar com eles, nao sem periodo.
  useFilterStations.setFiltersFromQuery(route?.query)
  await loadItems()
})

const allHeaders: any[] = [
  {
    key: 'id',
    align: 'start',
    title: 'Id'
  },
  {
    key: 'name',
    align: 'start',
    title: 'Nome'
  },
  {
    key: 'date',
    align: 'start',
    title: 'Data'
  },
  {
    key: 'hours',
    align: 'start',
    title: 'Horário'
  },
  {
    key: 'value',
    align: 'start',
    title: 'Valor',
    sortable: false,
    value: (row: RowConsultingResponse) => consultValueCell(row)
  },
  {
    key: 'schedule',
    align: 'start',
    title: 'Escala'
  },
  {
    key: 'sectorName',
    align: 'start',
    title: 'Setor',
    sortable: false,
    value: (row: RowConsultingResponse) => catalogDisplayName(rowSector(row), '—')
  },
  {
    key: 'specialtyName',
    align: 'start',
    title: 'Especialidade',
    sortable: false,
    value: (row: RowConsultingResponse) => catalogDisplayName(rowSpecialty(row), '—')
  },
  {
    key: 'timezone',
    align: 'start',
    title: 'Fuso horário',
    sortable: false
  },
  {
    key: 'startHour',
    align: 'start',
    title: 'Início'
  },
  {
    key: 'endHour',
    align: 'start',
    title: 'Fim'
  },
  {
    key: 'ourTeam',
    align: 'start',
    title: 'Nossa equipe'
  },
  {
    key: 'stationType',
    align: 'start',
    title: 'Tipo',
    sortable: false
  },
  {
    key: 'inCash',
    align: 'start',
    title: 'Á vista',
    sortable: false
  },
  {
    key: 'actions',
    align: 'end',
    title: '',
    sortable: false
  }
]

const mobileHeaderKeys = ['name', 'date', 'hours', 'value', 'schedule', 'actions']

const visibleHeaders = isDashboardV2
  ? allHeaders
  : allHeaders.filter((h) => !SECTOR_SPECIALTY_HEADER_KEYS.includes(h.key))

const headers = computed(() => {
  if (isMobile.value) {
    return visibleHeaders.filter((h) => mobileHeaderKeys.includes(h.key))
  }
  return withCoverageHeaders(visibleHeaders, consulting.value.rows)
})

const page = ref(1)
const pageCount = computed(() => {
  return Math.ceil(consulting.value.totalRecords / itemsPerPage)
})

const changePage = async (p: number) => {
  serverParams.value = Object.assign({}, serverParams.value, { page: p })
  await loadItems()
}

const isLoading = ref(false)
const isGlobalSelectAll = ref(false)

const serverParams = ref<ConsultingRequest>({
  columnFilters: {},
  sort: [],
  page: 1,
  perPage: 10
})

const modalStore = useModalStore()
const selected = ref<number[]>([])

const stationStore = useStationStore()
const { consulting, consultingLoadError } = storeToRefs(stationStore)

// Evita repetir o fallback genérico como detalhe sob o título fixo (mesmo
// padrão loadErrorDetail do Dashboard/Index.vue).
const consultingLoadErrorDetail = computed(() =>
  consultingLoadError.value && consultingLoadError.value !== CONSULTING_LOAD_ERROR
    ? consultingLoadError.value
    : null
)

const hasSelection = computed(() => Boolean(selected.value.length || isGlobalSelectAll.value))

// A edição em massa é por empresa: sem empresa no filtro o modal abriria sem ter
// o que editar (e sem seleção, anunciando "Editando 0 plantões").
const canEditSelection = computed(() => hasSelection.value && Boolean(filters.value.company))

const editDisabledReason = computed(() =>
  hasSelection.value
    ? 'Para editar selecione uma empresa nos filtros'
    : 'Para editar selecione pelo menos um plantão'
)

const toggleUpdateModal = () => {
  if (!canEditSelection.value) return
  modalStore.toggleModal(EModals.UPDATE_BULK_STATION)
}
const toggleRemoveModal = () => {
  if (!hasSelection.value) return
  modalStore.toggleModal(EModals.REMOVE_BULK_STATION)
}

const toggleFilterModal = () => {
  modalStore.toggleModal(EModals.FILTER_STATION)
}

function openCheckAttempts(item: RowConsultingResponse) {
  modalStore.toggleModal(EModals.CHECK_ATTEMPTS, {
    stationId: item.id,
    doctorName: item.name,
    date: item.date,
    hours: item.hours,
    schedule: item.schedule
  })
}

// Colunas sintéticas da tabela que não existem na entidade DoctorStation do backend.
// "hours" (início–fim) é ordenada pelo campo real startHour; as demais não são ordenáveis.
const SORT_FIELD_ALIASES: Record<string, string> = {
  hours: 'startHour'
}

const changeSort = async (sort: { key: string; order: string }[]) => {
  serverParams.value = Object.assign({}, serverParams.value, {
    sort: sort.map((s) => ({ field: SORT_FIELD_ALIASES[s.key] ?? s.key, type: s.order }))
  })
  await loadItems()
}

async function loadItems() {
  selected.value = []
  isGlobalSelectAll.value = false

  if (!hasPeriod.value) {
    stationStore.clearConsulting()
    isLoading.value = false
    return
  }

  isLoading.value = true
  serverParams.value.columnFilters = { ...filters.value }
  await stationStore.getListConsultingStations(serverParams.value).finally(() => {
    isLoading.value = false
    selected.value = []
    isGlobalSelectAll.value = false
  })
}

async function clearAllFilters() {
  useFilterStations.$reset()
  await loadItems()
}

function updateParams(newProps) {
  serverParams.value = Object.assign({}, serverParams.value, newProps)
}

async function handleApplyFilters(filters) {
  updateParams({ columnFilters: filters })
  await loadItems()
}

watch(isGlobalSelectAll, (newVal) => {
  if (newVal) {
    selected.value = consulting.value.rows.map((item) => item.id)
  } else {
    selected.value = []
  }
})

watch(
  () => consulting.value.rows,
  (newRows) => {
    if (isGlobalSelectAll.value) {
      selected.value = newRows.map((item) => item.id)
    }
  },
  { deep: true }
)

return (_ctx: any,_cache: any) => {
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_pagination = _resolveComponent("v-pagination")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", {
            class: _normalizeClass(["filter-btn", canEditSelection.value ? 'cursor-pointer' : 'cursor-default']),
            onClick: toggleUpdateModal
          }, [
            _createVNode(EditIcon, {
              width: "20",
              color: "#68688D"
            }),
            _createTextVNode(" Editar "),
            (!canEditSelection.value)
              ? (_openBlock(), _createBlock(_component_v_tooltip, {
                  key: 0,
                  activator: "parent",
                  location: "bottom"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(editDisabledReason.value), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ], 2),
          _createElementVNode("span", {
            onClick: toggleRemoveModal,
            class: _normalizeClass(["filter-btn", hasSelection.value ? 'cursor-pointer' : 'cursor-default'])
          }, [
            _createVNode(DeleteIcon, {
              width: "20",
              color: "#68688D"
            }),
            _createTextVNode("Excluir "),
            (!hasSelection.value)
              ? (_openBlock(), _createBlock(_component_v_tooltip, {
                  key: 0,
                  activator: "parent",
                  location: "bottom"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Para remover selecione pelo menos um plantão ")
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ], 2),
          _createElementVNode("span", {
            onClick: toggleFilterModal,
            class: _normalizeClass(["filter-btn", hasFilter.value ? 'border border-primary text-primary' : ''])
          }, [
            _createVNode(_component_v_icon, {
              size: "small",
              icon: _unref(mdiFilterOutline),
              color: hasFilter.value ? 'primary' : 'iconGrey'
            }, null, 8, ["icon", "color"]),
            _createTextVNode(" Filtros ")
          ], 2)
        ]),
        (_unref(consulting).totalRecords > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", null, [
                _createElementVNode("div", _hoisted_4, _toDisplayString(isGlobalSelectAll.value ? _unref(consulting).totalRecords : selected.value.length) + " plantões selecionados ", 1)
              ]),
              _createElementVNode("div", null, [
                _createVNode(_component_v_checkbox, {
                  modelValue: isGlobalSelectAll.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isGlobalSelectAll).value = $event)),
                  label: `${isGlobalSelectAll.value ? 'Desselecionar' : 'Selecionar'} todos plantões`,
                  density: "compact",
                  "hide-details": ""
                }, null, 8, ["modelValue", "label"])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(FiltersUsed),
          (hasFilter.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("button", {
                  onClick: clearAllFilters,
                  class: "text-sm font-bold text-primary hover:text-primary-darken-1 transition-colors"
                }, " Limpar filtros ")
              ]))
            : _createCommentVNode("", true)
        ]),
        (isLoading.value)
          ? (_openBlock(), _createBlock(CheckShiftSkeleton, { key: 0 }))
          : (_unref(consultingLoadError))
            ? (_openBlock(), _createBlock(ReportErrorState, {
                key: 1,
                title: _unref(CONSULTING_LOAD_ERROR),
                message: consultingLoadErrorDetail.value,
                onRetry: loadItems
              }, null, 8, ["title", "message"]))
            : (_unref(consulting).totalRecords > 0)
              ? (_openBlock(), _createBlock(_unref(VDataTable), {
                  key: 2,
                  modelValue: selected.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selected).value = $event)),
                  class: "overflow-hidden text-left text-sm",
                  hover: true,
                  headers: headers.value,
                  items: _unref(consulting).rows,
                  "item-value": "id",
                  "items-per-page": itemsPerPage,
                  "onUpdate:sortBy": changeSort,
                  "onUpdate:page": changePage,
                  "show-select": "",
                  "no-data-text": "Nenhum dado encontrado"
                }, {
                  [`item.name`]: _withCtx(({ item }) => [
                    (_unref(isUnidentifiedCoverage)(item.raw))
                      ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_unref(UNIDENTIFIED_NAME_LABEL)), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(item.raw.name), 1))
                  ]),
                  [`item.actions`]: _withCtx(({ item }) => [
                    _createVNode(_component_v_btn, {
                      icon: "",
                      size: "small",
                      variant: "text",
                      "data-testid": `check-attempts-button-${item.raw.id}`,
                      onClick: _withModifiers(($event: any) => (openCheckAttempts(item.raw)), ["stop"])
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          icon: _unref(mdiMapMarkerRadiusOutline),
                          size: "20"
                        }, null, 8, ["icon"]),
                        _createVNode(_component_v_tooltip, {
                          activator: "parent",
                          location: "bottom"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Tentativas de check")
                          ]),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1032, ["data-testid", "onClick"])
                  ]),
                  bottom: _withCtx(() => [
                    _createElementVNode("div", _hoisted_10, [
                      _createVNode(_component_v_pagination, {
                        modelValue: page.value,
                        "onUpdate:modelValue": [
                          _cache[1] || (_cache[1] = ($event: any) => ((page).value = $event)),
                          changePage
                        ],
                        "total-visible": 3,
                        length: pageCount.value,
                        density: "comfortable",
                        color: "primary"
                      }, null, 8, ["modelValue", "length"])
                    ])
                  ]),
                  _: 2
                }, 1032, ["modelValue", "headers", "items"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createVNode(_component_v_icon, {
                    class: "mb-4 text-gray-500",
                    size: "large",
                    icon: _unref(mdiOfficeBuildingPlusOutline)
                  }, null, 8, ["icon"]),
                  _createElementVNode("h3", _hoisted_12, _toDisplayString(emptyStateTitle.value), 1),
                  _createElementVNode("p", _hoisted_13, _toDisplayString(emptyStateMessage.value), 1),
                  _createVNode(_component_v_btn, {
                    class: "ma-2 font-semibold",
                    color: "primary",
                    "data-testid": "consult-open-filters-button",
                    onClick: toggleFilterModal
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Abrir Filtros ")
                    ]),
                    _: 1
                  })
                ]))
      ])
    ]),
    _createVNode(UpdateBulkStationModal, {
      ids: selected.value,
      isSelectAll: isGlobalSelectAll.value,
      totalRows: _unref(consulting).totalRecords,
      filters: _unref(filters),
      rows: _unref(consulting).rows,
      onRefreshData: loadItems
    }, null, 8, ["ids", "isSelectAll", "totalRows", "filters", "rows"]),
    _createVNode(RemoveBulkStationModal, {
      ids: selected.value,
      "is-select-all": isGlobalSelectAll.value,
      filters: _unref(filters),
      "total-items-to-delete": isGlobalSelectAll.value ? _unref(consulting).totalRecords : selected.value.length,
      onRefreshData: loadItems
    }, null, 8, ["ids", "is-select-all", "filters", "total-items-to-delete"]),
    _createVNode(CheckAttemptsModal),
    (_openBlock(), _createBlock(_Teleport, { to: "#app" }, [
      _createVNode(FilterStationModal, {
        "show-sector-specialty": _unref(isDashboardV2),
        "show-coverage-status": "",
        onApplyFilters: handleApplyFilters
      }, null, 8, ["show-sector-specialty"])
    ]))
  ], 64))
}
}

})